import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { onOpenLogoutModal } from "../../../shared/components/GlobalLogoutBus/globalLogoutBus";
import { logger } from "../../../shared/utils/logger.client";
import "./ForceLogoutModal.css";
import { useNavigate } from "react-router-dom";
import { logout } from "../utils/userSessionStorage";
import { handleRedirectBack } from "../../../shared/utils/navigationUtils";
import { useTranslation } from "react-i18next";

const WAIT_SECONDS = 10;

function shouldSuppressLogoutModal(): boolean {
  const isRedirecting = sessionStorage.getItem("oidcRedirect") === "1";
  const suppressed = localStorage.getItem("suppressLogoutModal") === "1";
  return isRedirecting || suppressed;
}

export default function ForceLogoutModal() {
  const [visible, setVisible] = useState(false);
  const { t } = useTranslation();
  const [processing, setProcessing] = useState(false);
  const [message, setMessage] = useState(t("forceLogout.defaultMessage"));
  const [countdown, setCountdown] = useState(WAIT_SECONDS);
  const navigate = useNavigate();
  const timerRef = useRef<number | null>(null);

  useEffect(() => {
    return onOpenLogoutModal(({ message }) => {
      if (shouldSuppressLogoutModal()) {
        logger.debug("[ForceLogoutModal] Suppressed during OIDC redirect.");
        return;
      }

      if (message) setMessage(message);
      setCountdown(WAIT_SECONDS);
      setVisible(true);
      document.body.classList.add("force-logout-open");
      logger.debug("[ForceLogoutModal] opened");
    });
  }, []);

  useEffect(() => {
    if (!visible) return;
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    timerRef.current = window.setInterval(() => {
      setCountdown((c) => {
        if (c <= 1) {
          clearInterval(timerRef.current!);
          timerRef.current = null;
          void handleConfirm();
          return 0;
        }
        return c - 1;
      });
    }, 1000);

    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [visible]);

  const cleanup = () => document.body.classList.remove("force-logout-open");

  const handleConfirm = async () => {
    if (processing) return;
    try {
      setProcessing(true);
      sessionStorage.removeItem("evidenceButtonLabelsById");
      sessionStorage.removeItem("evidenceDownloadResultsById");
      await logout(navigate);
      await handleRedirectBack(navigate, true);
    } catch (err) {
      logger.error("[ForceLogoutModal] Failed to logout", err);
    } finally {
      setProcessing(false);
      cleanup();
      setVisible(false);
    }
  };

  const handleCancel = () => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    setVisible(false);
    cleanup();
  };

  if (!visible) return null;

  const modal = (
      <div
          className="overlay-force-logout"
          id="BOX_FORCE_LOGOUT_OVERLAY"
          role="dialog"
          aria-modal="true"
          aria-labelledby="TXT_FORCE_LOGOUT_HEADER"
      >
        <div className="popup popup-force-logout">
          <h2 className="header" id="TXT_FORCE_LOGOUT_HEADER">{t("forceLogout.title")}</h2>

          <p id="TXT_FORCE_LOGOUT_MESSAGE">
            {message}
            {!processing && (
                <span style={{ display: "block", marginTop: "0.5rem" }}>
              {t("forceLogout.countdownPrefix")} <strong>{countdown}</strong> {t("forceLogout.countdownSuffix")}
            </span>
            )}
            {processing && (
                <span style={{ display: "block", marginTop: "0.5rem" }}>
              {t("forceLogout.processingMessage")}
            </span>
            )}
          </p>

          <div className="buttonContainer">
            <button
                className="cancelButton"
                id="BTN_FORCE_LOGOUT_CANCEL"
                onClick={handleCancel}
                disabled={processing}
            >
              {t("forceLogout.cancel")}
            </button>
            <button
                className="consentButton"
                id="BTN_FORCE_LOGOUT_CONFIRM"
                onClick={handleConfirm}
                disabled={processing}
            >
              {processing ? t("forceLogout.confirmProcessing") : t("forceLogout.confirm")}
            </button>
          </div>
        </div>
      </div>
  );

  return createPortal(modal, document.body);
}
